import React, { FC, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import {
  MetricsLineChart,
  MetricsStackedBarChart,
  formatDurationToMinutes,
} from '@/apps/legacy/components/portal/MetricsChart'
import {
  useMetricsAnalyticsQuery,
  usePharmacyListQuery,
  generateDateRangeArray,
  mapDataToDateRange,
} from '@/features/internal/metrics'
import { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

type PeriodType = 'daily' | 'weekly' | 'monthly'

interface TrendsTabProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  selectedPharmacyId: string
}

export const TrendsTab: FC<TrendsTabProps> = ({
  startDate,
  endDate,
  selectedPharmacyId,
}) => {
  const [period, setPeriod] = useState<PeriodType>('daily')

  const { pharmacyData } = usePharmacyListQuery()

  // リクエストパラメータを構築
  const requestParams = useMemo(() => {
    if (!startDate || !endDate || !pharmacyData) return null

    const dateRange = {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    }

    // 未選択は「絞らない」= キーを送らない。本部なら全社、それ以外は自店になる
    const targetParams =
      selectedPharmacyId && selectedPharmacyId !== ''
        ? { pharmacy_ids: [selectedPharmacyId] }
        : {}

    return {
      date_range: dateRange,
      period,
      ...targetParams,
    }
  }, [startDate, endDate, selectedPharmacyId, pharmacyData, period])

  const {
    data: analytics,
    isLoading: loading,
    error: queryError,
  } = useMetricsAnalyticsQuery({
    params: requestParams!,
    enabled: !!requestParams,
  })

  const error = queryError?.message ?? null

  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: PeriodType | null
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  // データ変換関数（メモ化）
  const formatMainTrendData = useMemo(() => {
    if (!analytics?.trends || !startDate || !endDate) return []

    const dateRange = generateDateRangeArray(startDate, endDate)

    // データをMapに変換（高速アクセスのため）
    const visitsMap = new Map<string, number>()
    const soapMap = new Map<string, number>()
    const copyMap = new Map<string, number>()

    analytics?.trends?.visits?.forEach((item) => {
      visitsMap.set(item.date, item.count)
    })
    analytics?.trends?.soap_generations?.forEach((item) => {
      soapMap.set(item.date, item.count)
    })
    analytics?.trends?.copies?.forEach((item) => {
      copyMap.set(item.date, item.count)
    })

    // 日付範囲にデータをマッピング
    return dateRange.map(({ dateStr, displayDate }) => {
      const visitCount = visitsMap.get(dateStr) || 0
      const soapCount = soapMap.get(dateStr) || 0
      const copyCount = copyMap.get(dateStr) || 0

      // 包含関係を正しく表現するため、差分を計算
      return {
        日付: displayDate,
        コピー実施: copyCount,
        SOAP生成のみ: soapCount - copyCount,
        来局のみ: visitCount - soapCount,
      }
    })
  }, [analytics?.trends, startDate, endDate])

  const formatDurationTrendData = useMemo(() => {
    if (!analytics?.trends?.durations || !startDate || !endDate) return []

    const dateRange = generateDateRangeArray(startDate, endDate)
    const data = mapDataToDateRange(
      dateRange,
      analytics?.trends?.durations,
      (item) => item.avg_duration || 0,
      0
    )

    return data.map((item) => ({
      日付: item.日付,
      平均時間: item.value,
    }))
  }, [analytics?.trends?.durations, startDate, endDate])

  const formatExchangeTrendData = useMemo(() => {
    if (!analytics?.trends?.exchange_counts || !startDate || !endDate) return []

    const dateRange = generateDateRangeArray(startDate, endDate)
    const data = mapDataToDateRange(
      dateRange,
      analytics?.trends?.exchange_counts,
      (item) => item.avg_count || 0,
      0
    )

    return data.map((item) => ({
      日付: item.日付,
      平均回数: item.value,
    }))
  }, [analytics?.trends?.exchange_counts, startDate, endDate])

  return (
    <Box>
      {loading && <LoadingIndicator />}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 期間タイプ選択 */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          backgroundColor: colorTokens.background.paper,
          borderRadius: 2,
          border: `1px solid ${colorTokens.border.light}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colorTokens.text.secondary,
              mr: 1,
            }}
          >
            表示期間:
          </Typography>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
          >
            <ToggleButton value="daily">日次</ToggleButton>
            <ToggleButton value="weekly">週次</ToggleButton>
            <ToggleButton value="monthly">月次</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* グラフ表示 */}
      {analytics && (
        <>
          {/* 1行目: 来局数・SOAP生成・コピー数の推移と指導時間の推移 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  来局数・SOAP生成・コピー数推移
                </Typography>
                <MetricsStackedBarChart
                  data={formatMainTrendData}
                  dataKeys={['コピー実施', 'SOAP生成のみ', '来局のみ']}
                  xAxisKey="日付"
                  height={350}
                  colors={[
                    colorTokens.blue[600],
                    colorTokens.blue[300],
                    colorTokens.blue[200],
                  ]}
                  graphStackBottom="first" // コピー実施を一番下に
                  legendReverse={true} // 凡例を逆順（来局のみを最初に）
                  tooltipReverse={true} // ツールチップを棒グラフと同じ順序に
                  yAxisUnit="件"
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  平均指導時間の推移
                </Typography>
                <MetricsLineChart
                  data={formatDurationTrendData}
                  dataKey="平均時間"
                  xAxisKey="日付"
                  height={350}
                  color={colorTokens.blue[600]}
                  yAxisTickFormatter={formatDurationToMinutes}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* 2行目: やりとり回数の推移 */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  平均やりとり回数の推移
                </Typography>
                <MetricsLineChart
                  data={formatExchangeTrendData}
                  dataKey="平均回数"
                  xAxisKey="日付"
                  height={350}
                  color={colorTokens.blue[600]}
                  yAxisUnit="回"
                />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
